// The account's Orpheus billing settings and its own provider key (v3 `orpheusApiKey.ts`,
// ORP-25170/25171/25172; backend ORP-25296). Every route is account owner/admin only, bare JSON
// (not enveloped), and `suppressGlobalErrorHandler`: the settings pane and the consent screen word
// every failure themselves — a key the provider refused is the user's to fix, not a global toast.
import type {
  OrpheusApiKeySummaryDto,
  OrpheusKeyCheckResultDto,
  OrpheusProvider,
  OrpheusSettingsDto,
} from '../models/orpheusKey.types'

import { appHttp } from '@/shared/http'

const url = (accountId: number | string, path: string): string => `/accounts/${accountId}/${path}`

export class OrpheusSettingsApi {
  constructor(private readonly http: typeof appHttp = appHttp) {}

  private readonly cfg = { suppressGlobalErrorHandler: true } as const

  async fetch(accountId: number | string): Promise<OrpheusSettingsDto> {
    return (await this.http.get<OrpheusSettingsDto>(url(accountId, 'orpheus-settings/'), this.cfg))
      .data
  }

  /** Resolves the stored value, which is what the switch shows afterwards. */
  async setFallback(accountId: number | string, enabled: boolean): Promise<boolean> {
    const response = await this.http.patch<{ fallback_to_credits?: boolean }>(
      url(accountId, 'orpheus-settings/'),
      { fallback_to_credits: enabled },
      this.cfg,
    )
    return response.data?.fallback_to_credits === true
  }

  /**
   * Checks the key with one real provider call before storing it, so it can take a few seconds.
   * A key the provider refuses is never stored — the rejection carries `ORPHEUS_KEY_*`.
   */
  async saveKey(
    accountId: number | string,
    provider: OrpheusProvider,
    apiKey: string,
  ): Promise<OrpheusApiKeySummaryDto | null> {
    const response = await this.http.post<{ api_key?: OrpheusApiKeySummaryDto | null }>(
      url(accountId, 'orpheus-api-key/'),
      { provider, api_key: apiKey },
      this.cfg,
    )
    return response.data?.api_key ?? null
  }

  async removeKey(accountId: number | string): Promise<void> {
    await this.http.delete(url(accountId, 'orpheus-api-key/'), this.cfg)
  }

  /** Always 200 while a key is stored; `check.ok` says whether the provider accepted it now. */
  async checkKey(accountId: number | string): Promise<OrpheusKeyCheckResultDto> {
    return (
      await this.http.post<OrpheusKeyCheckResultDto>(
        url(accountId, 'orpheus-api-key/check/'),
        {},
        this.cfg,
      )
    ).data
  }
}
